/**
 * @file     leadConversionTiers.ts
 * @readme   ../CLAUDE.md
 * @purpose  Server-safe (no 'use client') value-tier table for lead types. The browser-only
 *           gtag firing logic lives in leadConversion.ts; this file holds the pure data so
 *           server route handlers can resolve a tier without dragging the client module.
 */

import type { LeadType as SubmittedLeadType } from '@/services/leads/leadTypes';

/** Every submitted lead type, plus the stress-test PDF download, which has its
 *  own route and never enters the /api/leads union. Deriving from the leads
 *  union makes a new lead type without a tier or category a compile error. */
export type LeadType = SubmittedLeadType | 'mortgage-stress-test-pdf';

export type ConversionTier = 'HIGH' | 'MID' | 'SOFT';

export interface TierConfig {
  value: number;
  labelEnvKey: 'googleAdsConversionLabelHigh' | 'googleAdsConversionLabelMid' | 'googleAdsConversionLabelSoft';
}

export const TIERS: Record<ConversionTier, TierConfig> = {
  HIGH: { value: 80, labelEnvKey: 'googleAdsConversionLabelHigh' },
  MID:  { value: 40, labelEnvKey: 'googleAdsConversionLabelMid' },
  SOFT: { value: 20, labelEnvKey: 'googleAdsConversionLabelSoft' },
};

export const LEAD_TYPE_TO_TIER: Record<LeadType, ConversionTier> = {
  'seller': 'HIGH',
  'buyer': 'HIGH',
  // Name, email, phone and a lender position: a buyer lead with its numbers.
  'property-finder': 'HIGH',
  'deal-finder': 'HIGH',
  // PDF download = full stress test completed AND name/email captured to receive a document.
  // Highest-intent lead this site produces from the calculator funnel.
  'mortgage-stress-test-pdf': 'HIGH',
  // Stress test (without PDF) = qualified buyer signal (income + debts + verdict in payload).
  'mortgage-stress-test': 'MID',
  'landlord': 'MID',
  // Partial deal-finder = user gave us their email mid-funnel via "Email me my progress"
  // before completing the funnel. Soft signal: real intent + working contact, but no
  // qualification yet. Treated as a SOFT conversion in Google Ads.
  'deal-finder-partial': 'SOFT',
  'newsletter': 'SOFT',
  'mortgage-calculator': 'SOFT',
  'academy': 'SOFT',
  // New account creation: soft signal — has email, no qualification yet.
  'user-signup': 'SOFT',
  // Abandoned VOW registration: contact details but no account and no access.
  // Softest signal we file — real intent, nothing qualified.
  'vow-partial': 'SOFT',
  'vow-partial-buyer': 'SOFT',
  // A licensed REALTOR® asking to claim an area. Highest-intent lead the site
  // produces: a named professional, a named brokerage, and a specific area,
  // against a paid program. The /for-agents ad groups bid on this event.
  'agent-enquiry': 'HIGH',
  // Pre-approval readiness check: real intent and a working contact, but no
  // verified income or debt. Same tier as the stress test.
  'pre-approval': 'MID',
  // Purchasing power + neighbourhood match: same banded finances as the
  // pre-approval estimate, plus property shape and area priorities.
  'buyer-match': 'MID',
  // Someone writing to Ray directly: a named person asking for a reply.
  'contact': 'HIGH',
  // A completed rent-vs-buy walkthrough (situation, intent, rent, timeline)
  // ending in a request to talk it through. Same depth as pre-approval and
  // buyer-match, above the one-screen mortgage calculator.
  'rent_vs_buy': 'MID',
  // Email-only saved-search alert from the SEO pages: same signal as a
  // newsletter signup, no qualification.
  'shortlist': 'SOFT',
  // A rental viewing request forwarded to the listing brokerage: Ray does not
  // represent the renter, so it is a soft signal, not a client.
  'tenant': 'SOFT',
  // A viewing request on a home Ray lists for its owner: a named renter Ray
  // answers himself, same depth as a landlord enquiry.
  'owner_listing': 'MID',
};

export function getLeadConversionValue(type: LeadType): { tier: ConversionTier; value: number } {
  const tier = LEAD_TYPE_TO_TIER[type] ?? 'SOFT';
  if (type === 'agent-enquiry') {
    return { tier, value: AGENT_ENQUIRY_CONVERSION_VALUE };
  }
  return { tier, value: TIERS[tier].value };
}

// ──────────────────────────────────────────────────────────────
// One GA4 event per submission. Each lead type reports under exactly one
// `generate_lead_<category>` name, carrying `lead_type` as a parameter for
// anything finer-grained than the four Ads-facing categories. There is no
// separate umbrella event: a dashboard or Ads import that wants "all
// consumer leads" sums the four category events instead of reading a fifth
// one that duplicated every submission.
// ──────────────────────────────────────────────────────────────

export type LeadCategory = 'seller' | 'buyer' | 'realtor' | 'signup';

/** Closed union so a typo in a call site is a compile error, not a silent
 *  new GA4 event name. */
export type LeadEventName = `generate_lead_${LeadCategory}`;

const LEAD_TYPE_TO_CATEGORY: Record<LeadType, LeadCategory> = {
  'seller': 'seller',
  'buyer': 'buyer',
  'property-finder': 'buyer',
  'deal-finder': 'buyer',
  'deal-finder-partial': 'buyer',
  'buyer-match': 'buyer',
  'landlord': 'seller',
  // Mortgage tools are buyer-funnel qualification steps, not their own Ads
  // category.
  'mortgage-calculator': 'buyer',
  'mortgage-stress-test': 'buyer',
  'mortgage-stress-test-pdf': 'buyer',
  'pre-approval': 'buyer',
  'newsletter': 'signup',
  'academy': 'signup',
  'user-signup': 'signup',
  'vow-partial': 'signup',
  'vow-partial-buyer': 'buyer',
  // A licensed REALTOR® enquiring about the agent program. A business
  // enquiry, never counted as a consumer seller/buyer lead.
  'agent-enquiry': 'realtor',
  // A free-text message from /contact carries no funnel qualification; the
  // buyer category is the closest general-inquiry lane.
  'contact': 'buyer',
  'rent_vs_buy': 'buyer',
  'shortlist': 'signup',
  // Renters are not buyers or sellers; filed under signup so they never feed
  // the buyer or seller bidding.
  'tenant': 'signup',
  'owner_listing': 'signup',
};

/** The one GA4 event name a lead type reports under. */
export function getLeadEventName(type: LeadType): LeadEventName {
  return `generate_lead_${LEAD_TYPE_TO_CATEGORY[type]}`;
}

/**
 * Expected value of one agent enquiry, in CAD, for Ads bidding.
 *
 * NOT the contract value. A signed agent pays $2,500 up front plus $500 a
 * month, but this event fires on an enquiry, and most enquiries will not sign.
 * This is roughly the up-front fee discounted by a conservative close rate, so
 * bidding compares an agent enquiry against a consumer lead on expected value
 * rather than on headline price. One constant to change once real close-rate
 * data exists.
 */
export const AGENT_ENQUIRY_CONVERSION_VALUE = 250;
